import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, List, ListItem, makeStyles, Typography } from '@material-ui/core'
import { Form } from 'react-final-form'
import _ from 'lodash'

import ChecklistAnswerRadioGroup from './ChecklistAnswerRadioGroup'
import Dialog from '../../../control/dialog/dialog'
import AnswerComment from './AnswerComment'

import useTranslate from '../../../i18n/useTranslate'
import useChecklistStore from '../../../stores/checklist/useChecklistStore'
import useChecklistAction from '../../../stores/checklist/useChecklistAction'
import useCheckPermission from '../../../utilities/checkPermission'
import useManageProjectStore from '../../../stores/manageProject/useManageProjectStore'

import type {
    ChecklistAnswerComment,
    ChecklistComment,
    ChecklistTopic,
    SubChecklistComment,
} from '../../../services/web/useChecklist'
import type { CheckListInTopicType } from '../../../services/web/types/requestWebServiceTypes'

export interface ChecklistDialogProps {
    title: string
    topicID: number
    committeeID: number
    isReadOnly?: boolean
    onChange?: (values: ChecklistTopic) => void
}

function ChecklistDialog(props: ChecklistDialogProps) {
    const { title, topicID, committeeID, isReadOnly = false, onChange } = props

    const classes = useStyles()
    const translate = useTranslate()
    const currentLanguage = translate.languageStore.currentLanguage
    const { getChecklist: fetchChecklist, setChecklist } = useChecklistAction()
    const { checklist: checklistTopicList } = useChecklistStore()
    const { projectData } = useManageProjectStore()
    const checkPermissionHook = useCheckPermission()
    const isViewMode = checkPermissionHook.chkProjectStepViewMode(projectData)

    const dialogRef = useRef<Record<string, Function>>()
    const [hasChecklist, setHasChecklist] = useState<boolean>(false)
    const [initialValues, setInitialValues] = useState<ChecklistTopic>()

    /**
     * Ensures default answers are marked as checked and
     * checklist/sub-checklist parents are checked if they have answers.
     */
    function normalizeChecked(checklistTopic: ChecklistTopic): ChecklistTopic {
        const normalizeAnswers = (answers: ChecklistAnswerComment[]) => {
            if (answers.every((answer) => answer.checked === false)) {
                answers.forEach((answer) => {
                    answer.checked = answer.isDefault
                })
            }
        }
        checklistTopic.data.forEach((checklist) => {
            if (checklist.answers) {
                checklist.checked = true
                normalizeAnswers(checklist.answers)
            }
            checklist.children?.forEach((subChecklist) => {
                if (subChecklist.answers) {
                    subChecklist.checked = true
                    normalizeAnswers(subChecklist.answers)
                }
            })
        })

        return checklistTopic
    }

    function onOpen() {
        dialogRef.current?.openDialog()
        const checklistTopic = checklistTopicList.find((e) => e.topicID === topicID) as ChecklistTopic
        setInitialValues(normalizeChecked(_.cloneDeep(checklistTopic)))
    }

    function onClose() {
        dialogRef.current?.closeDialog()
    }

    const onSubmit = useCallback(
        (values: ChecklistTopic) => {
            setChecklist(values as CheckListInTopicType)
            dialogRef?.current?.closeDialog()
            onChange && onChange(values)
        },
        [dialogRef, setChecklist],
    )

    // Fetch checklist when committeeID and projectData are available
    useEffect(() => {
        if (!committeeID || !projectData) return
        fetchChecklist(projectData.submittedID, committeeID, projectData.irb)
    }, [committeeID, projectData])

    // Track whether the checklist exists for this topic
    useEffect(() => {
        setHasChecklist(checklistTopicList.some((e) => e.topicID === topicID))
    }, [checklistTopicList])

    if (!hasChecklist) return null

    return (
        <>
            <Button className={classes.checklistButton} onClick={onOpen}>
                {translate.get('LABEL.CHECKLIST_FORM')}
            </Button>

            <Dialog
                onRef={(funcs) => (dialogRef.current = funcs)}
                title={title}
                type=""
                maxWidth="md"
                content={
                    <Form onSubmit={onSubmit} initialValues={initialValues}>
                        {({ values, handleSubmit }) => (
                            <form onSubmit={handleSubmit}>
                                {/*  Checklist */}
                                <List>
                                    {values.data.map((checklist: ChecklistComment, checklistIndex: number) => (
                                        <ListItem key={checklist.checklistID}>
                                            <Box display="flex" flexDirection="column" width="1">
                                                <Typography>
                                                    {`${checklistIndex + 1}. ${
                                                        checklist[
                                                            `name${currentLanguage.toUpperCase()}` as keyof ChecklistComment
                                                        ]
                                                    }`}
                                                </Typography>

                                                {checklist.children && checklist.children.length > 0 ? (
                                                    <List>
                                                        {checklist.children.map(
                                                            (
                                                                subChecklist: SubChecklistComment,
                                                                subChecklistIndex: number,
                                                            ) => (
                                                                <ListItem key={subChecklist.checklistID}>
                                                                    <Box
                                                                        display="flex"
                                                                        flexDirection="column"
                                                                        width="1"
                                                                    >
                                                                        <Typography>
                                                                            {`${checklistIndex + 1}.${
                                                                                subChecklistIndex + 1
                                                                            }. ${
                                                                                subChecklist[
                                                                                    `name${currentLanguage.toUpperCase()}` as keyof SubChecklistComment
                                                                                ]
                                                                            }`}
                                                                        </Typography>

                                                                        {subChecklist.answers &&
                                                                        subChecklist.answers.length > 0 ? (
                                                                            <>
                                                                                <ChecklistAnswerRadioGroup
                                                                                    name={`data[${checklistIndex}].children[${subChecklistIndex}].answers`}
                                                                                    answers={subChecklist.answers}
                                                                                    disabled={isViewMode || isReadOnly}
                                                                                />
                                                                                <AnswerComment
                                                                                    name={`data[${checklistIndex}].children[${subChecklistIndex}].comment`}
                                                                                    initialValues={subChecklist.comment}
                                                                                    answers={subChecklist.answers}
                                                                                    disabled={isViewMode || isReadOnly}
                                                                                />
                                                                            </>
                                                                        ) : null}
                                                                    </Box>
                                                                </ListItem>
                                                            ),
                                                        )}
                                                    </List>
                                                ) : null}

                                                {checklist.answers && checklist.answers.length > 0 ? (
                                                    <>
                                                        <ChecklistAnswerRadioGroup
                                                            name={`data[${checklistIndex}].answers`}
                                                            answers={checklist.answers}
                                                            disabled={isViewMode || isReadOnly}
                                                        />
                                                        <AnswerComment
                                                            name={`data[${checklistIndex}].comment`}
                                                            initialValues={checklist.comment}
                                                            answers={checklist.answers}
                                                            disabled={isViewMode || isReadOnly}
                                                        />
                                                    </>
                                                ) : null}
                                            </Box>
                                        </ListItem>
                                    ))}
                                </List>

                                {/* Actions */}
                                <Box display="flex" justifyContent="center" style={{ gap: 16 }}>
                                    {isReadOnly || isViewMode ? (
                                        <Button onClick={onClose} variant="contained" color="primary">
                                            {translate.get('BUTTON.CLOSE')}
                                        </Button>
                                    ) : (
                                        <>
                                            <Button onClick={onClose} variant="contained">
                                                {translate.get('BUTTON.CLOSE')}
                                            </Button>

                                            <Button color="primary" type="submit" variant="contained">
                                                {translate.get('UM.EXTERNAL-REGISTER.OK')}
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            </form>
                        )}
                    </Form>
                }
            />
        </>
    )
}

const useStyles = makeStyles(() => ({
    checklistButton: {
        fontSize: '17px',
        color: '#0062FF',
        textDecoration: 'underline',
    },
}))

export default ChecklistDialog
